import { useEffect, useState } from "react";
import axios from "../axios";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState("saved");

  const [form, setForm] = useState({
    name: "",
    type: "text",
    message: "",
    media_url: "",
    button_text: "",
    button_url: "",
    meta_template_name: "",
    language_code: "en_US",
    header_type: "none",
    button_type: "none",
    status: "local",
  });

  const fetchTemplates = async () => {
    try {
      const res = await axios.get("/templates");
      setTemplates(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchMetaTemplates = async () => {
    try {
      const res = await axios.get("/templates/meta-templates");
      setMetaTemplates(res.data.templates);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchMetaTemplates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "type" && (value === "image" || value === "image_button")
        ? { header_type: "image" }
        : {}),
      ...(name === "type" && (value === "text" || value === "button")
        ? { header_type: "none", media_url: "" }
        : {}),
      ...(name === "button_text" || name === "button_url"
        ? { button_type: "url" }
        : {}),
    }));
  };

  const createTemplate = async () => {
    if (!form.name || !form.message) {
      alert("Template name and message required");
      return;
    }
    try {
      await axios.post("/templates", form);
      alert("Template created ✅");
      setForm({
        name: "",
        type: "text",
        message: "",
        media_url: "",
        button_text: "",
        button_url: "",
        meta_template_name: "",
        language_code: "en_US",
        header_type: "none",
        button_type: "none",
        status: "local",
      });
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.msg || "Template create failed");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`/templates/${id}`);
      fetchTemplates();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const submitToMeta = async (id) => {
    try {
      const res = await axios.post(`/templates/${id}/submit-meta`);
      alert(res.data.msg || "Submitted to Meta");
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.error?.message || err.response?.data?.msg || "Meta submit failed");
    }
  };

  const checkStatus = async (id) => {
    try {
      const res = await axios.get(`/templates/${id}/check-status`);
      alert(`Status: ${res.data.status}`);
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.msg || "Status check failed");
    }
  };

  const typeConfig = {
    text: { label: "Text", color: "tag-blue" },
    image: { label: "Image", color: "tag-pink" },
    button: { label: "Button", color: "tag-green" },
    image_button: { label: "Image + Button", color: "tag-purple" },
  };

  const statusConfig = {
    local: { label: "Local", color: "status-gray" },
    pending: { label: "Pending", color: "status-orange" },
    approved: { label: "Approved", color: "status-green" },
    rejected: { label: "Rejected", color: "status-red" },
    APPROVED: { label: "Approved", color: "status-green" },
    PENDING: { label: "Pending", color: "status-orange" },
    REJECTED: { label: "Rejected", color: "status-red" },
  };

  const hasMedia = form.type === "image" || form.type === "image_button";
  const hasButton = form.type === "button" || form.type === "image_button";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        .templates-page * {
          font-family: 'Sora', sans-serif;
          box-sizing: border-box;
        }

        .templates-page {
          background: #f0f2f8;
          min-height: 100vh;
          padding: 28px 32px;
        }

        /* Header */
        .tmpl-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 12px;
          flex-wrap: wrap;
        }

        .tmpl-title-wrap {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .tmpl-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          box-shadow: 0 4px 14px rgba(37,99,235,0.35);
          flex-shrink: 0;
        }

        .tmpl-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.5px;
        }

        .tmpl-subtitle {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 400;
          margin-top: 1px;
        }

        .tmpl-count-badge {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 6px 16px;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          white-space: nowrap;
        }

        .tmpl-count-badge span { color: #2563eb; }

        /* Main grid */
        .tmpl-grid {
          display: grid;
          grid-template-columns: 420px 1fr;
          gap: 16px;
          align-items: start;
        }

        /* Cards */
        .tmpl-card {
          background: #fff;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 2px 16px rgba(15,23,42,0.07);
          border: 1px solid #e8ecf4;
          margin-bottom: 16px;
        }

        .tmpl-card-header {
          padding: 14px 20px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          gap: 10px;
          background: #fafbff;
        }

        .tmpl-card-icon {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
        }

        .icon-blue { background: #eff6ff; }
        .icon-purple { background: #f5f3ff; }
        .icon-green { background: #f0fdf4; }

        .tmpl-card-title {
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          flex: 1;
        }

        .tmpl-card-body {
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* Form fields */
        .field-label {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 5px;
        }

        .field-group {
          display: flex;
          flex-direction: column;
        }

        .tmpl-input {
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 14px;
          font-size: 13.5px;
          font-family: 'Sora', sans-serif;
          color: #0f172a;
          outline: none;
          background: #f8fafc;
          transition: all 0.2s;
          width: 100%;
        }

        .tmpl-input:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
        }

        .tmpl-input::placeholder { color: #94a3b8; }

        .tmpl-select {
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 36px 10px 14px;
          font-size: 13.5px;
          font-family: 'Sora', sans-serif;
          color: #0f172a;
          outline: none;
          background: #f8fafc;
          appearance: none;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
        }

        .tmpl-select:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
        }

        .select-wrap {
          position: relative;
        }

        .select-arrow {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 12px;
          pointer-events: none;
        }

        .tmpl-textarea {
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 11px 14px;
          font-size: 13.5px;
          font-family: 'Sora', sans-serif;
          color: #0f172a;
          outline: none;
          background: #f8fafc;
          resize: vertical;
          min-height: 110px;
          line-height: 1.6;
          transition: all 0.2s;
          width: 100%;
        }

        .tmpl-textarea:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
        }

        .tmpl-textarea::placeholder { color: #94a3b8; }

        .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        /* Upload area */
        .upload-area {
          border: 1.5px dashed #cbd5e1;
          border-radius: 12px;
          padding: 18px;
          text-align: center;
          cursor: pointer;
          transition: all 0.18s;
          background: #f8fafc;
          position: relative;
        }

        .upload-area:hover {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .upload-area input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
          width: 100%;
          height: 100%;
        }

        .upload-icon { font-size: 24px; margin-bottom: 6px; }
        .upload-text { font-size: 13px; font-weight: 500; color: #64748b; }
        .upload-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }

        .img-preview {
          margin-top: 10px;
          width: 100%;
          max-height: 160px;
          object-fit: cover;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        /* Save button */
        .btn-save {
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          font-family: 'Sora', sans-serif;
          transition: all 0.18s;
          box-shadow: 0 3px 12px rgba(37,99,235,0.3);
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .btn-save:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 18px rgba(37,99,235,0.4);
        }

        /* Tabs */
        .tab-row {
          display: flex;
          gap: 8px;
          margin-bottom: 14px;
        }

        .tab-btn {
          padding: 8px 18px;
          border-radius: 20px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          font-size: 13px;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .tab-btn:hover {
          border-color: #2563eb;
          color: #2563eb;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          border-color: transparent;
          color: #fff;
          box-shadow: 0 3px 10px rgba(37,99,235,0.3);
        }

        .tab-count {
          background: rgba(255,255,255,0.25);
          border-radius: 10px;
          padding: 1px 7px;
          font-size: 11px;
        }

        .tab-btn:not(.active) .tab-count {
          background: #f1f5f9;
          color: #94a3b8;
        }

        /* Template list card */
        .tmpl-list-card {
          background: #fff;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 2px 16px rgba(15,23,42,0.07);
          border: 1px solid #e8ecf4;
        }

        .tmpl-list-header {
          padding: 14px 20px;
          border-bottom: 1px solid #f1f5f9;
          background: #fafbff;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        /* Template item */
        .tmpl-item {
          padding: 16px 20px;
          border-bottom: 1px solid #f8fafc;
          transition: background 0.15s;
        }

        .tmpl-item:last-child { border-bottom: none; }
        .tmpl-item:hover { background: #fafbff; }

        .tmpl-item-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          gap: 10px;
          flex-wrap: wrap;
        }

        .tmpl-item-name {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 5px;
          word-break: break-word;
        }

        .tmpl-item-tags {
          display: flex;
          gap: 5px;
          flex-wrap: wrap;
        }

        .tag {
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 6px;
        }

        .tag-blue { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .tag-pink { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; }
        .tag-green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .tag-purple { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }

        .status-gray { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
        .status-orange { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
        .status-green { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
        .status-red { background: #fff5f5; color: #dc2626; border: 1px solid #fecaca; }

        .tmpl-item-actions {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
          flex-wrap: wrap;
        }

        .action-btn {
          border: none;
          border-radius: 8px;
          padding: 5px 11px;
          font-size: 11.5px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Sora', sans-serif;
          transition: all 0.15s;
          white-space: nowrap;
        }

        .action-btn-meta {
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
        }

        .action-btn-meta:hover {
          background: #2563eb;
          color: #fff;
        }

        .action-btn-status {
          background: #f0fdf4;
          color: #059669;
          border: 1px solid #bbf7d0;
        }

        .action-btn-status:hover {
          background: #059669;
          color: #fff;
        }

        .action-btn-delete {
          background: #fff5f5;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .action-btn-delete:hover {
          background: #dc2626;
          color: #fff;
        }

        .tmpl-item-msg {
          font-size: 13px;
          color: #475569;
          line-height: 1.6;
          margin-top: 8px;
          background: #f8fafc;
          border-radius: 10px;
          padding: 10px 12px;
          border: 1px solid #f1f5f9;
          word-break: break-word;
        }

        .tmpl-item-img {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          margin-top: 8px;
        }

        .tmpl-btn-preview {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin-top: 8px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
          font-size: 12px;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 8px;
        }

        /* Meta template item */
        .meta-item {
          padding: 14px 20px;
          border-bottom: 1px solid #f8fafc;
        }

        .meta-item:last-child { border-bottom: none; }

        .meta-item-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 5px;
          word-break: break-word;
        }

        .meta-item-row {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .meta-lang {
          font-size: 11px;
          font-weight: 500;
          color: #64748b;
          font-family: 'JetBrains Mono', monospace;
        }

        /* Empty state */
        .empty-state {
          padding: 48px 24px;
          text-align: center;
          color: #94a3b8;
        }

        .empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.35; }
        .empty-text { font-size: 14px; }

        /* ===================== TABLET ===================== */
        @media (max-width: 1024px) {
          .tmpl-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 860px) {
          .templates-page {
            padding: 20px 18px;
          }

          .two-col {
            gap: 8px;
          }
        }

        /* ===================== MOBILE ===================== */
        @media (max-width: 640px) {
          .templates-page {
            padding: 16px 12px;
          }

          .tmpl-header {
            margin-bottom: 18px;
          }

          .tmpl-icon {
            width: 40px;
            height: 40px;
            font-size: 17px;
            border-radius: 12px;
          }

          .tmpl-title {
            font-size: 18px;
          }

          .tmpl-subtitle {
            font-size: 11px;
          }

          .tmpl-count-badge {
            padding: 5px 12px;
            font-size: 12px;
          }

          .tmpl-card {
            border-radius: 16px;
            margin-bottom: 12px;
          }

          .tmpl-card-header {
            padding: 12px 14px;
          }

          .tmpl-card-body {
            padding: 14px;
          }

          .two-col {
            grid-template-columns: 1fr;
          }

          .tab-row {
            gap: 6px;
          }

          .tab-btn {
            flex: 1;
            justify-content: center;
            padding: 8px 10px;
            font-size: 12.5px;
          }

          .tmpl-list-header {
            padding: 12px 14px;
          }

          .tmpl-item {
            padding: 14px;
          }

          .tmpl-item-top {
            flex-direction: column;
          }

          .tmpl-item-actions {
            width: 100%;
          }

          .action-btn {
            flex: 1;
            text-align: center;
          }

          .meta-item {
            padding: 12px 14px;
          }
        }

        @media (max-width: 380px) {
          .tmpl-title {
            font-size: 16px;
          }

          .tmpl-icon {
            width: 36px;
            height: 36px;
            font-size: 15px;
          }
        }
      `}</style>

      <div className="templates-page">
        {/* Header */}
        <div className="tmpl-header">
          <div className="tmpl-title-wrap">
            <div className="tmpl-icon">🗂</div>
            <div>
              <div className="tmpl-title">Message Templates</div>
              <div className="tmpl-subtitle">Create and manage WhatsApp templates</div>
            </div>
          </div>
          <div className="tmpl-count-badge">
            Total: <span>{templates.length}</span>
          </div>
        </div>

        <div className="tmpl-grid">
          {/* ── Left: Create Form ── */}
          <div>
            <div className="tmpl-card">
              <div className="tmpl-card-header">
                <div className="tmpl-card-icon icon-blue">✏️</div>
                <div className="tmpl-card-title">Create Template</div>
              </div>
              <div className="tmpl-card-body">

                {/* Name */}
                <div className="field-group">
                  <div className="field-label">Template Name</div>
                  <input
                    name="name"
                    className="tmpl-input"
                    placeholder="e.g. offer_promo_text"
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>

                {/* Type */}
                <div className="field-group">
                  <div className="field-label">Type</div>
                  <div className="select-wrap">
                    <select name="type" className="tmpl-select" value={form.type} onChange={handleChange}>
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="button">Button</option>
                      <option value="image_button">Image + Button</option>
                    </select>
                    <span className="select-arrow">▾</span>
                  </div>
                </div>

                {/* Message */}
                <div className="field-group">
                  <div className="field-label">Message</div>
                  <textarea
                    name="message"
                    className="tmpl-textarea"
                    placeholder="Hello {name}, your offer is ready!"
                    value={form.message}
                    onChange={handleChange}
                  />
                </div>

                {/* Meta template name + Language */}
                <div className="two-col">
                  <div className="field-group">
                    <div className="field-label">Meta Template Name</div>
                    <input
                      name="meta_template_name"
                      className="tmpl-input"
                      placeholder="offer_image_button"
                      value={form.meta_template_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="field-group">
                    <div className="field-label">Language Code</div>
                    <input
                      name="language_code"
                      className="tmpl-input"
                      placeholder="en_US"
                      value={form.language_code}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="field-group">
                  <div className="field-label">Status</div>
                  <div className="select-wrap">
                    <select name="status" className="tmpl-select" value={form.status} onChange={handleChange}>
                      <option value="local">Local Only</option>
                      <option value="pending">Pending Approval</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <span className="select-arrow">▾</span>
                  </div>
                </div>

                {/* Image Upload */}
                {hasMedia && (
                  <div className="field-group">
                    <div className="field-label">Upload Image</div>
                    <div className="upload-area">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append("image", file);
                          try {
                            const res = await axios.post("/templates/upload-image", formData, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            setForm((prev) => ({ ...prev, media_url: res.data.url, header_type: "image" }));
                            alert("Image uploaded ✅");
                          } catch {
                            alert("Image upload failed");
                          }
                        }}
                      />
                      <div className="upload-icon">🖼</div>
                      <div className="upload-text">Click to upload image</div>
                      <div className="upload-sub">PNG, JPG, WEBP supported</div>
                    </div>
                    {form.media_url && (
                      <img src={form.media_url} alt="preview" className="img-preview" />
                    )}
                  </div>
                )}

                {/* Button fields */}
                {hasButton && (
                  <div className="field-group">
                    <div className="field-label">Button</div>
                    <div className="two-col">
                      <input
                        name="button_text"
                        className="tmpl-input"
                        placeholder="Button Text"
                        value={form.button_text}
                        onChange={handleChange}
                      />
                      <input
                        name="button_url"
                        className="tmpl-input"
                        placeholder="https://..."
                        value={form.button_url}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                {/* Save */}
                <button className="btn-save" onClick={createTemplate}>
                  💾 Save Template
                </button>
              </div>
            </div>
          </div>

          {/* ── Right: Lists ── */}
          <div>
            {/* Tabs */}
            <div className="tab-row">
              <button
                className={`tab-btn ${activeTab === "saved" ? "active" : ""}`}
                onClick={() => setActiveTab("saved")}
              >
                🗂 Saved
                <span className="tab-count">{templates.length}</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "meta" ? "active" : ""}`}
                onClick={() => setActiveTab("meta")}
              >
                🌐 Meta
                <span className="tab-count">{metaTemplates.length}</span>
              </button>
            </div>

            {/* Saved Templates */}
            {activeTab === "saved" && (
              <div className="tmpl-list-card">
                <div className="tmpl-list-header">Saved Templates</div>

                {templates.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🗂</div>
                    <div className="empty-text">No templates yet. Create one!</div>
                  </div>
                ) : (
                  templates.map((t) => {
                    const tc = typeConfig[t.type] || { label: t.type, color: "tag-blue" };
                    const sc = statusConfig[t.status] || { label: t.status, color: "status-gray" };
                    return (
                      <div key={t.id} className="tmpl-item">
                        <div className="tmpl-item-top">
                          <div>
                            <div className="tmpl-item-name">{t.name}</div>
                            <div className="tmpl-item-tags">
                              <span className={`tag ${tc.color}`}>{tc.label}</span>
                              <span className={`tag ${sc.color}`}>{sc.label}</span>
                            </div>
                          </div>
                          <div className="tmpl-item-actions">
                            <button className="action-btn action-btn-meta" onClick={() => submitToMeta(t.id)}>
                              ↑ Meta
                            </button>
                            <button className="action-btn action-btn-status" onClick={() => checkStatus(t.id)}>
                              ⟳ Status
                            </button>
                            <button className="action-btn action-btn-delete" onClick={() => deleteTemplate(t.id)}>
                              🗑
                            </button>
                          </div>
                        </div>

                        <div className="tmpl-item-msg">{t.message}</div>

                        {t.media_url && (
                          <img src={t.media_url} alt="template" className="tmpl-item-img" />
                        )}

                        {t.button_text && (
                          <div className="tmpl-btn-preview">
                            🔗 {t.button_text}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Meta Templates */}
            {activeTab === "meta" && (
              <div className="tmpl-list-card">
                <div className="tmpl-list-header">Meta Templates</div>

                {metaTemplates.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🌐</div>
                    <div className="empty-text">No Meta templates found</div>
                  </div>
                ) : (
                  metaTemplates.map((t, index) => {
                    const sc = statusConfig[t.status] || { label: t.status, color: "status-gray" };
                    return (
                      <div key={index} className="meta-item">
                        <div className="meta-item-name">{t.name}</div>
                        <div className="meta-item-row">
                          <span className={`tag ${sc.color}`}>{sc.label}</span>
                          <span className="meta-lang">🌐 {t.language}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}